import {autorun} from 'mobx'
import {map, minBy, values, isString} from 'lodash'
import {CSSTransitionProps} from 'react-transition-group/CSSTransition'
import zenscroll from 'zenscroll'
import {OverlayMode} from './OverlayMode'
import {Block} from './Block'
import {WidgetFlag} from './WidgetFlag'
import {WidgetApi} from './WidgetApi'

///////////////////////////////////////////////////////////////////////////////

export type WidgetUiArgs = {
  api: WidgetApi
}

type ScrollIntoViewOptions = {
  offset?: number
  duration?: number
}

///////////////////////////////////////////////////////////////////////////////

export class WidgetUi {
  api: WidgetApi

  async init(args: WidgetUiArgs) {
    const {api} = args
    const {computedConfig} = api

    this.api = api

    this.initOverlays()

    autorun(() => {
      // observe layout and replace appropriate css classes on body
      computedConfig.overlayMode

      let bodyClasses = map(
        values(OverlayMode),
        (v) => 'tamaro-widget-overlay-' + v,
      )
      document.body.classList.remove(...bodyClasses)
      document.body.classList.add(
        'tamaro-widget-overlay-' + computedConfig.overlayMode,
      )
    })
  }

  initOverlays() {
    let {flags} = this.api

    document.addEventListener('click', (event) => {
      let element = event.target as HTMLElement

      if (!element.hasAttribute('data-overlay')) {
        element = element.closest<HTMLElement>('[data-overlay]')
      }

      if (element) {
        event.preventDefault()
        const overlayName = element.getAttribute('data-overlay')

        if (overlayName === 'data-protection-policy') {
          flags.add(WidgetFlag.SHOW_OVERLAY_DATA_PROTECTION_POLICY)
        }
      }
    })
  }

  /////////////////////////////////////////////////////////////////////////////

  getTopElementId() {
    return this.generateUniqueElementId('widget-top-element-')
  }

  getWidgetElement(fallback: boolean = false): HTMLElement | Document {
    const topElement = document.querySelector('#' + this.getTopElementId())

    if (topElement) {
      return topElement.closest<HTMLElement>('#tamaro-widget')
    }

    return fallback ? document : null
  }

  /////////////////////////////////////////////////////////////////////////////

  focusWidgetElementBySelector(...selectors: string[]) {
    setTimeout(() => {
      const widgetElement = this.getWidgetElement()

      if (widgetElement) {
        for (let selector of selectors) {
          const element = widgetElement.querySelector<HTMLElement>(selector)

          if (element) {
            element.focus()

            break
          }
        }
      }
    })
  }

  focusElementBySelector(...selectors: string[]) {
    setTimeout(() => {
      for (let selector of selectors) {
        const element = document.querySelector<HTMLElement>(selector)

        if (element) {
          element.focus()

          break
        }
      }
    })
  }

  generateUniqueElementId(id: string): string {
    return `${id}-widget`
  }

  getCreditCardIframeTargetId() {
    return 'credit-card-iframe-widget'
  }

  async scrollToTop() {
    return this.scrollTo('#' + this.getTopElementId())
  }

  async scrollTo(
    target: string | HTMLElement,
    options: ScrollIntoViewOptions = {},
  ): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const el = isString(target)
          ? document.querySelector<HTMLElement>(target)
          : target
        const scrollingElement = document.scrollingElement || document.body
        const onDone = () => {
          scrollingElement.classList.remove(
            'tamaro-widget-scroll-behavior-auto',
          )
          resolve()
        }

        if (el) {
          const {uiScrollOffset, uiScrollDuration} = this.api.computedConfig
          let {offset, duration} = options

          offset = offset || uiScrollOffset
          duration = duration || uiScrollDuration

          if (el) {
            scrollingElement.classList.add('tamaro-widget-scroll-behavior-auto')
            zenscroll.toY(zenscroll.getTopOf(el) - offset, duration, onDone)
          }
        }
      })
    })
  }

  async scrollToTopInvalidField() {
    const topErrorMessageElement = await this.getTopErrorMessageElement()
    const formBlock = topErrorMessageElement?.closest<HTMLElement>(
      '.form-block',
    )
    const {uiScrollOffset} = this.api.computedConfig

    if (formBlock) {
      return this.scrollTo(formBlock)
    } else if (topErrorMessageElement) {
      return this.scrollTo(topErrorMessageElement, {
        offset: uiScrollOffset + 100,
      })
    }
  }

  async focusTopInvalidField() {
    const topErrorMessageElement = await this.getTopErrorMessageElement()
    const formBlock = topErrorMessageElement?.closest<HTMLElement>(
      '.form-block',
    )
    const block = topErrorMessageElement?.closest<HTMLElement>('.widget-block')

    if (formBlock) {
      formBlock
        .querySelector<HTMLElement>(
          'input[aria-invalid="true"], select[aria-invalid="true"], textarea[aria-invalid="true"]',
        )
        ?.focus()
    }

    if (block && block.classList.contains('payment-payment-methods-block')) {
      block.querySelector<HTMLElement>('.payment-method[tabindex="0"]')?.focus()
    }
  }

  async getErrorMessageElements(): Promise<HTMLElement[]> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const errorMessageElements = map(
          this.getWidgetElement(true).querySelectorAll<HTMLElement>(
            '.error-message:not(:empty)',
          ),
          (el) => el,
        )

        resolve(errorMessageElements)
      })
    })
  }

  async getTopErrorMessageElement(): Promise<HTMLElement> {
    return minBy(
      await this.getErrorMessageElements(),
      (el) => el.getBoundingClientRect()['top'],
    )
  }

  /////////////////////////////////////////////////////////////////////////////

  getBlockCSSTransitionProps(name: Block): CSSTransitionProps {
    const {uiTransitionTimeout: timeout} = this.api.computedConfig

    return {
      timeout,
      appear: true,
      classNames: 'fade-in',
    }
  }
}

///////////////////////////////////////////////////////////////////////////////

export function createWidgetUi(): WidgetUi {
  return new WidgetUi()
}
